/**
 * Forecast Period Selector
 *
 * Allows users to select time periods for forecast viewing:
 * - Week, 2 weeks, Month, 3 months, Quarter, Year
 * - Previous/Next navigation for quarters and years
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { getDateRangeForPeriod } from '../../services/forecastService'

const PERIOD_OPTIONS = [
  { value: 'week', label: 'This Week' },
  { value: '2weeks', label: '2 Weeks' },
  { value: 'month', label: 'This Month' },
  { value: '3months', label: '3 Months' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' }
]

export default function ForecastPeriodSelector({
  period,
  offset = 0,
  onPeriodChange,
  onOffsetChange,
  className = ''
}) {
  // Get the label for the current period/offset
  const periodInfo = getDateRangeForPeriod(period, offset)
  const showNavigation = period === 'quarter' || period === 'year'

  const handlePreviousPeriod = () => {
    onOffsetChange(offset - 1)
  }

  const handleNextPeriod = () => {
    // Don't allow navigating into the future for non-predictive periods
    if (offset < 0) {
      onOffsetChange(offset + 1)
    }
  }

  const handlePeriodSelect = (newPeriod) => {
    onPeriodChange(newPeriod)
    // Reset offset when changing period type
    if (offset !== 0) {
      onOffsetChange(0)
    }
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Period Type Selector */}
      <Select value={period} onValueChange={handlePeriodSelect}>
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="Select period" />
        </SelectTrigger>
        <SelectContent>
          {PERIOD_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Navigation for quarter/year */}
      {showNavigation && (
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handlePreviousPeriod}
            title={`Previous ${period}`}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <span className="text-sm text-muted-foreground min-w-[100px] text-center">
            {periodInfo.label}
          </span>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleNextPeriod}
            disabled={offset >= 0}
            title={`Next ${period}`}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Date range display */}
      {!showNavigation && (
        <span className="text-xs text-muted-foreground">
          {new Date(periodInfo.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          {' - '}
          {new Date(periodInfo.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
      )}
    </div>
  )
}
